/* ═══════════════════════════════════════════
   PANDA PCs — Theme JavaScript
   ═══════════════════════════════════════════

   ── PUBLIC HOOKS FOR OTHER SECTIONS ──
   Everything below is exposed on window.PandaMotion so section files never
   need their own IntersectionObserver, their own rAF loop, or their own
   scroll listener. There is ONE of each in the theme; adding a second is the
   thing this round exists to stop.

     class="panda-reveal"
       Fades up 14px, once, when it enters view, then unobserves itself.
       Styled and reduced-motion-guarded in panda-base.css (.panda-reveal,
       and the `transform: none !important` block at the bottom of that file).
       Variants that already exist: --left --right --scale --blur.

     data-reveal-stagger[="45"]   (on a PARENT)
       Every .panda-reveal inside it gets an automatic transition-delay of
       index x N ms, capped at 6 steps so a 20-card grid still finishes in
       270ms. Replaces hand-written .panda-reveal--delay-1..6 classes in
       markup. The delay is written as an inline style, which the
       prefers-reduced-motion block in panda-base.css overrides with
       `transition-delay: 0s !important` — so a runtime change of the OS
       preference is honoured with no JS involved.

     data-spotlight               (on a SECTION)
       A soft gradient that follows the pointer, exposed to CSS as the custom
       properties --hhero-sx / --hhero-sy (percentages within the element).
       The section supplies the gradient; this file only supplies the numbers.
       Pointer-only and reduced-motion-only-off — see the block below.

     data-header-sentinel         (ONE per page, optional)
       A zero-height marker. The header gains .is-scrolled once this marker
       has passed above the bottom edge of the header. Put it at the END of
       the hero. If no page supplies one, this file injects an out-of-flow
       1px marker at the top of the document, which reproduces the old
       "scrolled more than a few pixels" behaviour.

     window.PandaMotion.reveal(root)   re-scan after injecting HTML
     window.PandaMotion.reduceMotion() live read of the OS preference
     window.PandaMotion.onScroll(fn)   join the single passive scroll rAF
     window.PandaMotion.lockScroll() / .unlockScroll()
       Reference-counted iOS-safe body lock. Use these instead of setting
       document.body.style.overflow, which does not lock Safari on iOS and
       loses the reader's scroll position on close.
   ═══════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Motion Preference ──
     Two flags in this file used to read

       var prefersReducedMotion = false; // force animations everywhere by owner request

     which is not a guard, it is a guard-shaped comment. Every scroll-driven
     effect below sat behind one of them, so the whole file ran at full travel
     for a visitor who had asked their OS for less: inertia scrolling that
     detaches from the trackpad, a parallax layer, an ornament driven 120px
     sideways, and a header that jumps out of the way. Those are exactly the
     effects that cause vestibular symptoms - travel tied to scroll, which the
     reader cannot stop without also stopping reading.

     The preference is read LIVE rather than snapshotted, so toggling it in the
     OS takes effect on the next frame instead of on the next reload. The one
     thing that cannot be re-decided per frame is Lenis, so it is started and
     stopped from the same query's change event.

     Reduced motion is not NO motion: hover colour, focus rings, the cart count
     pop, the loading spinner and opacity fades all stay. Only travel goes.
  */
  var mm = typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null;

  var motionQuery = mm ? mm('(prefers-reduced-motion: reduce)') : null;
  /* The theme setting Motion > "Force animation for everyone" stamps
     .panda-force-motion onto <html>. It is the single override point for every
     script-driven effect in the theme, mirroring the CSS escape
     html:not(.panda-force-motion) that scopes all 26 reduced-motion blocks.
     Read live rather than cached so toggling it in the theme editor takes
     effect without a reload. */
  function forceMotion() {
    return !!(document.documentElement &&
              document.documentElement.classList.contains('panda-force-motion'));
  }

  function reduceMotion() {
    if (forceMotion()) return false;
    return !!(motionQuery && motionQuery.matches);
  }

  /* ── Pointer capability ──
     BOTH halves are required and neither is sufficient. A stylus reports
     (pointer: fine) with (hover: none); a Windows touchscreen laptop reports
     (hover: hover) with (pointer: coarse) and would otherwise get every
     cursor-tracking effect in the theme driven by a fingertip. The old
     magnetic-button block checked (hover: hover) alone, which is why it is
     gone rather than fixed.

     Read LIVE, like the motion query — a Surface docked to a mouse changes
     this without a reload. */
  var fineQuery = mm ? mm('(hover: hover) and (pointer: fine)') : null;
  function finePointer() {
    return !!(fineQuery && fineQuery.matches);
  }

  function onMediaChange(query, fn) {
    if (!query) return;
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', fn);
    } else if (typeof query.addListener === 'function') {
      query.addListener(fn);
    }
  }

  /* ── One passive scroll listener for the whole theme ──
     There used to be three (parallax, scroll lines, header), each with its own
     rAF gate, so a scroll frame could schedule three separate callbacks that
     each read layout and then wrote to it. Subscribers now share one listener
     and one frame. Registration is lazy: if nothing subscribes — which is the
     case on the homepage once the header state moved to an observer — no
     scroll listener is ever attached at all. */
  var scrollSubs = [];
  var scrollQueued = false;
  var scrollBound = false;

  function scrollFrame() {
    scrollQueued = false;
    for (var i = 0; i < scrollSubs.length; i++) scrollSubs[i]();
  }
  function scrollTick() {
    if (scrollQueued) return;
    scrollQueued = true;
    requestAnimationFrame(scrollFrame);
  }
  function onScroll(fn) {
    scrollSubs.push(fn);
    if (!scrollBound) {
      scrollBound = true;
      window.addEventListener('scroll', scrollTick, { passive: true });
    }
  }

  /* ── iOS-safe body scroll lock ──
     document.body.style.overflow = 'hidden' does not stop scrolling in Safari
     on iOS, so opening the mobile menu left the page scrolling behind it, and
     the version that does work (position: fixed) throws the reader back to the
     top unless the offset is restored by hand. Reference counted so two
     overlays can't unlock each other. */
  var lockCount = 0;
  var lockedY = 0;

  function lockScroll() {
    if (lockCount++ > 0) return;
    lockedY = window.scrollY || window.pageYOffset || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = (-lockedY) + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    b.style.overflow = 'hidden';
    if (lenis) lenis.stop();
  }
  function unlockScroll() {
    if (lockCount === 0) return;
    if (--lockCount > 0) return;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    b.style.overflow = '';
    window.scrollTo(0, lockedY);
    if (lenis && !reduceMotion()) lenis.start();
  }

  /* ── Mobile Menu Toggle ── */
  var menuToggle = document.querySelector('[data-menu-toggle]');
  var mobileMenu = document.querySelector('[data-mobile-menu]');
  var menuOverlay = document.querySelector('[data-menu-overlay]');

  function closeMenu() {
    if (!mobileMenu || !mobileMenu.classList.contains('is-open')) return;
    mobileMenu.classList.remove('is-open');
    if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
    if (menuOverlay) menuOverlay.classList.remove('is-visible');
    unlockScroll();
  }

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', function () {
      if (mobileMenu.classList.contains('is-open')) {
        closeMenu();
        return;
      }
      mobileMenu.classList.add('is-open');
      menuToggle.setAttribute('aria-expanded', 'true');
      if (menuOverlay) menuOverlay.classList.add('is-visible');
      lockScroll();
    });

    if (menuOverlay) {
      menuOverlay.addEventListener('click', closeMenu);
    }
  }

  /* ── Cart Drawer ──
     All cart-drawer behavior (toggle, AJAX add, qty, remove) lives in the
     cart-drawer snippet so it stays self-contained. It still sets
     document.body.style.overflow directly; window.PandaMotion.lockScroll is
     exported above so that file can adopt the iOS-safe lock without owning a
     second copy of it. ── */

  /* ── Quantity Selectors ── */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.matches !== 'function') return;
    if (t.matches('[data-qty-minus]') || t.matches('[data-qty-plus]')) {
      var input = t.parentNode.querySelector('[data-qty-input]');
      if (!input) return;
      var val = parseInt(input.value, 10) || 1;
      if (t.matches('[data-qty-minus]')) {
        input.value = Math.max(1, val - 1);
      } else {
        input.value = val + 1;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ── Fluid Inertia Scrolling (Lenis) — DESKTOP POINTERS ONLY ──
     The vendored build is constructed with the default syncTouch:false, i.e.
     it does nothing whatsoever to touch scrolling — but its constructor still
     registers wheel/touchstart/touchmove/touchend on the window as NON-PASSIVE,
     unconditionally. A non-passive touchmove listener on the window forfeits
     the browser's compositor-thread scrolling fast path: every touch-scroll
     frame has to wait for JavaScript before the page can move. On a mid-range
     Android off a paid-social click that is felt directly as scroll latency,
     and it bought nothing, because syncTouch was off.

     So Lenis is only CONSTRUCTED behind (hover: hover) and (pointer: fine).
     The listeners exist only if the object does. The remaining mobile cost is
     the 12.8KB <script> tag itself in layout/theme.liquid — that file belongs
     to another agent this round; see the handoff note in the report.

     Second fix: the rAF loop used to run for the entire life of the page
     whether or not anything was scrolling, which keeps the main thread from
     ever idling. It is now started by a wake event and cancelled 250ms after
     the last scroll tick. */
  var lenis = null;
  var lenisRafId = null;
  var lenisIdleTimer = null;

  function lenisStartLoop() {
    if (!lenis || lenisRafId !== null) return;
    lenisRafId = requestAnimationFrame(function step(time) {
      lenis.raf(time);
      lenisRafId = requestAnimationFrame(step);
    });
  }
  function lenisStopLoop() {
    if (lenisRafId === null) return;
    cancelAnimationFrame(lenisRafId);
    lenisRafId = null;
  }

  if (window.Lenis && finePointer() && !reduceMotion()) {
    lenis = new Lenis({
      duration: 1.1,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true
    });

    // Lenis drives scrolling itself, and while it is running the browser does
    // NOT emit window 'scroll' events. Anything in the theme that listens for
    // scroll is therefore dead on arrival — that is why the sticky buy bar
    // never appeared. Re-broadcast Lenis's own scroll as a normal event so
    // ordinary listeners keep working, and expose the instance for scripts that
    // need to drive it (scrollTo, stop/start while the cart drawer is open —
    // lockScroll above finally does exactly that).
    window.pandaLenis = lenis;
    lenis.on('scroll', function () {
      window.dispatchEvent(new CustomEvent('panda:scroll'));
      if (lenisIdleTimer) clearTimeout(lenisIdleTimer);
      lenisIdleTimer = setTimeout(lenisStopLoop, 250);
    });

    var wake = function () { lenisStartLoop(); };
    window.addEventListener('wheel', wake, { passive: true });
    window.addEventListener('keydown', wake, { passive: true });
    window.addEventListener('pointerdown', wake, { passive: true });
    window.addEventListener('resize', wake, { passive: true });
    lenisStartLoop();

    // Inertia is the one effect here that cannot be re-decided per frame, so
    // it follows the preference by being stopped and started. Stopped, Lenis
    // hands scrolling back to the browser at its native 1:1 response.
    onMediaChange(motionQuery, function () {
      if (reduceMotion()) {
        lenis.stop();
        lenisStopLoop();
      } else {
        lenis.start();
        lenisStartLoop();
      }
    });
  }

  /* ── Smooth Scroll for Anchor Links ──
     The offset used to be a hardcoded -80 on the Lenis path and nothing at all
     on the native path, so on a phone — which now always takes the native path
     — #lineup landed underneath ~184px of announcement bar, header and build
     strip. The offset is measured from the real chrome at click time instead:
     two reads, on a click, once. */
  function chromeOffset() {
    var offset = 0;
    var bars = document.querySelectorAll('.panda-header, .panda-buildstrip');
    for (var i = 0; i < bars.length; i++) {
      var r = bars[i].getBoundingClientRect();
      // Only count chrome that is actually parked at the top of the viewport
      // right now — a header that has scrolled away must not pad the jump.
      if (r.height && r.bottom > 0 && r.top < 250) offset += r.height;
    }
    return offset + 12;
  }

  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener('click', function (e) {
      var href = this.getAttribute('href');
      if (!href || href === '#') return;
      var target = null;
      // An href like "#5" or "#a b" is a valid attribute and an invalid
      // selector; querySelector throws on it and would kill the handler.
      try { target = document.querySelector(href); } catch (err) { return; }
      if (!target) return;
      e.preventDefault();
      var pad = chromeOffset();
      if (lenis && !reduceMotion()) {
        lenisStartLoop();
        lenis.scrollTo(target, { offset: -pad });
        return;
      }
      // An animated jump is still several screens of travel the reader did
      // not ask for.
      var top = target.getBoundingClientRect().top + (window.scrollY || 0) - pad;
      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion() ? 'auto' : 'smooth'
      });
    });
  });

  /* ── Global Scroll Reveal ──
     ONE observer for the whole document, created lazily, shared by every
     element and every later re-scan. Elements unobserve themselves on fire, so
     nothing re-triggers on the way back up.

     The travel (14px), the curve and the reduced-motion behaviour all live in
     panda-base.css on .panda-reveal — this file only decides WHEN. That split
     is what lets the reduced-motion guard be a plain CSS media query instead
     of a branch here that has to be re-evaluated. */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('is-visible');
        revealObserver.unobserve(entries[i].target);
      }
    }, { threshold: 0.05, rootMargin: '0px 0px -40px 0px' });
    return revealObserver;
  }

  /* Automatic sibling stagger. Writes only — no layout is read — so this
     cannot thrash. Capped at 6 steps to match the .panda-reveal--delay-1..6
     ladder in panda-base.css: a 20-card grid must not take 900ms to finish
     arriving. Elements that already carry a --delay-N class are left alone. */
  function applyStagger(scope) {
    var groups = scope.querySelectorAll('[data-reveal-stagger]');
    for (var g = 0; g < groups.length; g++) {
      var step = parseFloat(groups[g].getAttribute('data-reveal-stagger')) || 45;
      var kids = groups[g].querySelectorAll('.panda-reveal');
      var n = 0;
      for (var k = 0; k < kids.length; k++) {
        var el = kids[k];
        if (el.getAttribute('data-panda-staggered') !== null) continue;
        el.setAttribute('data-panda-staggered', '');
        // getAttribute, not .className — on an SVG element className is an
        // SVGAnimatedString and has no indexOf.
        if ((el.getAttribute('class') || '').indexOf('panda-reveal--delay-') === -1) {
          var ms = Math.min(n, 5) * step;
          if (ms) el.style.transitionDelay = (ms / 1000).toFixed(3) + 's';
        }
        n++;
      }
    }
  }

  function initReveals(root) {
    var scope = (root && typeof root.querySelectorAll === 'function') ? root : document;
    applyStagger(scope);
    var els = scope.querySelectorAll('.panda-reveal:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      // Fallback: just show everything. .panda-reveal starts at opacity 0, so
      // failing open is the only acceptable failure mode.
      for (var i = 0; i < els.length; i++) els[i].classList.add('is-visible');
      return;
    }
    var io = getRevealObserver();
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  initReveals(document);
  // Re-scan after the Shopify theme editor swaps a section, or after any
  // section injects markup of its own.
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e && e.target ? e.target : document);
  });

  /* ── Scroll Parallax for [data-parallax] ──
     Two changes from the version that shipped: only elements currently near
     the viewport are in the working set (an observer maintains it, so an
     off-screen layer costs nothing per frame), and all the reads happen before
     any of the writes. Interleaving them — measure A, move A, measure B —
     invalidates layout on every element, so the browser has to recompute it
     synchronously N times per frame instead of once. */
  var parallaxEls = document.querySelectorAll('[data-parallax]');
  if (parallaxEls.length) {
    var parallaxLive = [];
    var parallaxReads = [];

    var parallaxIO = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var el = entries[i].target;
            var at = parallaxLive.indexOf(el);
            if (entries[i].isIntersecting && at === -1) parallaxLive.push(el);
            else if (!entries[i].isIntersecting && at !== -1) parallaxLive.splice(at, 1);
          }
        }, { rootMargin: '20% 0px 20% 0px' })
      : null;

    for (var pi = 0; pi < parallaxEls.length; pi++) {
      if (parallaxIO) parallaxIO.observe(parallaxEls[pi]);
      else parallaxLive.push(parallaxEls[pi]);
    }

    var updateParallax = function () {
      if (reduceMotion()) {
        // Park the layer at its neutral position rather than leaving it
        // wherever the last unreduced frame happened to put it.
        for (var r = 0; r < parallaxEls.length; r++) parallaxEls[r].style.transform = '';
        return;
      }
      if (!parallaxLive.length) return;
      // Pass 1 — read.
      parallaxReads.length = 0;
      for (var a = 0; a < parallaxLive.length; a++) {
        parallaxReads.push(parallaxLive[a].parentElement.getBoundingClientRect().top);
      }
      // Pass 2 — write.
      for (var b = 0; b < parallaxLive.length; b++) {
        var speed = parseFloat(parallaxLive[b].getAttribute('data-parallax')) || 0.2;
        parallaxLive[b].style.transform =
          'translateY(' + (parallaxReads[b] * speed).toFixed(1) + 'px) scale(1.12)';
      }
    };

    onScroll(updateParallax);
    updateParallax();
  }

  /* ── Scroll Lines ──
     The ornament used to be driven 120px sideways and 10px up and down across
     its travel - a decorative squiggle moving further than any content on the
     page, which is the same shouting this round is removing everywhere else,
     just spelled in motion instead of tracking. The draw-in is the part that
     carries anything; the drift is a hint now (24px / 4px), not a performance.

     Same read/write split as the parallax above: every getBoundingClientRect
     is taken before the first strokeDashoffset is written. */
  var scrollLines = document.querySelectorAll('[data-scroll-line]');
  if (scrollLines.length) {
    var lineParts = [];
    for (var li = 0; li < scrollLines.length; li++) {
      var wrap = scrollLines[li];
      var path = wrap.querySelector('path');
      var svg = wrap.querySelector('svg');
      if (path && svg) lineParts.push({ wrap: wrap, path: path, svg: svg });
    }

    var lineReads = [];
    var updateScrollLines = function () {
      var reduce = reduceMotion();
      if (reduce) {
        // Show the finished line immediately and never move it. This is the
        // same end state as the .panda-draw rules in panda-base.css, which
        // the inline styles set here would otherwise be fighting.
        for (var z = 0; z < lineParts.length; z++) {
          lineParts[z].path.style.strokeDashoffset = '0';
          lineParts[z].svg.style.transform = '';
        }
        return;
      }
      var vh = window.innerHeight;
      // Pass 1 — read.
      lineReads.length = 0;
      for (var a = 0; a < lineParts.length; a++) {
        lineReads.push(lineParts[a].wrap.getBoundingClientRect());
      }
      // Pass 2 — write.
      for (var b = 0; b < lineParts.length; b++) {
        var rect = lineReads[b];
        if (rect.bottom < -100 || rect.top > vh + 100) continue;
        // 0 when the line enters the bottom of the screen, 1 when it leaves the top
        var progress = (vh - rect.top) / (vh + rect.height);
        progress = Math.min(1, Math.max(0, progress));
        // draw in over the first 70% of travel, stay drawn after
        var draw = Math.min(1, progress / 0.7);
        lineParts[b].path.style.strokeDashoffset = (1 - draw).toFixed(3);
        var sway = Math.sin(progress * Math.PI * 2) * 4;
        lineParts[b].svg.style.transform =
          'translateX(' + ((progress - 0.5) * 24).toFixed(1) + 'px) translateY(' + sway.toFixed(1) + 'px)';
      }
    };

    if (lineParts.length) {
      onScroll(updateScrollLines);
      updateScrollLines();
    }
  }

  /* ── Pointer Spotlight for [data-spotlight] ──
     Replaces the magnetic buttons, which are deleted. Magnetism moved the buy
     button out from under the cursor — the worst thing a control can do to
     someone with a motor impairment — and it did it from an unthrottled
     mousemove that read getBoundingClientRect and then wrote style.transform
     inside the handler, i.e. one forced synchronous layout per pointer event,
     ~140 per second on a high-polling-rate mouse.

     What replaces it moves nothing. It writes two percentages to CSS custom
     properties and lets a gradient in the section's own stylesheet do the
     rest. Guards, in order:

       1. (hover: hover) AND (pointer: fine), read live. On touch, pointermove
          fires all the way through a scroll drag, so an ungated version would
          chase the finger while the page moves and then leave a highlight
          stuck where the finger lifted.
       2. pointerType must be 'mouse' — a stylus on a hybrid device passes the
          media query but should not paint a hover state.
       3. prefers-reduced-motion. A gradient that tracks the cursor is travel.
       4. The move listener is attached on pointerenter and REMOVED on
          pointerleave, so the cost when the pointer is anywhere else on the
          page is exactly zero listeners and zero frames.

     One rAF per frame while hovering, doing one read and two custom-property
     writes on one element. Custom properties consumed only by a background
     gradient repaint that layer; they do not invalidate layout. */
  var spotEls = document.querySelectorAll('[data-spotlight]');

  function initSpotlights() {
    // Nothing is bound — not even the scroll subscriber — until the device
    // reports a real cursor. A phone runs zero lines of this block.
    if (!spotEls.length || !finePointer()) return;
    for (var si = 0; si < spotEls.length; si++) {
      if (spotEls[si].getAttribute('data-spot-bound') !== null) continue;
      spotEls[si].setAttribute('data-spot-bound', '');
      bindSpotlight(spotEls[si]);
    }
  }
  initSpotlights();
  // A tablet docked to a mouse mid-session flips this without a reload.
  onMediaChange(fineQuery, initSpotlights);

  function bindSpotlight(el) {
    var px = 0;
    var py = 0;
    var queued = false;
    var live = false;

    function frame() {
      queued = false;
      if (!live) return;
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      el.style.setProperty('--hhero-sx', (((px - r.left) / r.width) * 100).toFixed(1) + '%');
      el.style.setProperty('--hhero-sy', (((py - r.top) / r.height) * 100).toFixed(1) + '%');
    }
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(frame);
    }
    function move(e) {
      px = e.clientX;
      py = e.clientY;
      queue();
    }
    function end() {
      if (!live) return;
      live = false;
      el.classList.remove('is-spot-live');
      el.removeEventListener('pointermove', move);
    }

    el.addEventListener('pointerenter', function (e) {
      if (live) return;
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (!finePointer() || reduceMotion()) return;
      live = true;
      px = e.clientX;
      py = e.clientY;
      el.classList.add('is-spot-live');
      el.addEventListener('pointermove', move, { passive: true });
      queue();
    });
    el.addEventListener('pointerleave', end);

    // Keep the highlight anchored to the element while the page scrolls under
    // a stationary cursor. Costs one function call per scroll frame when the
    // pointer is not inside the element, and nothing else.
    onScroll(function () { if (live) queue(); });
    onMediaChange(motionQuery, function () { if (reduceMotion()) end(); });
    onMediaChange(fineQuery, function () { if (!finePointer()) end(); });
  }

  /* ── Loading Feedback on Cart/Checkout Submits ── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('form[action*="/cart"], form[action*="/checkout"]')) return;
    var btn = form.querySelector('[type="submit"]');
    if (btn && !btn.classList.contains('is-loading')) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      // Safety: restore if navigation doesn't happen (e.g. validation error)
      setTimeout(function () {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }, 8000);
    }
  });

  /* ── Header State ──
     .is-scrolled (the hairline + shadow + opaque ground) is driven by an
     IntersectionObserver on a sentinel, not by a scroll handler. The old
     version ran a scroll callback on every frame of every scroll on every page
     for the whole session to compare a number against 8. The observer fires
     twice per page: once when the hero leaves, once if it comes back.

     The sentinel sits at the END of the hero (sections/home-hero.liquid ships
     one), so the treatment arrives when the hero has passed rather than after
     eight pixels. Pages with no hero get an injected marker at the top of the
     document, which reproduces the old behaviour. The marker is absolutely
     positioned and 1px, so it is out of flow and cannot move anything.

     .is-scrolled is colour and a hairline, not travel, so it is NOT gated on
     reduced motion. */
  var header = document.querySelector('.panda-header');

  if (header && 'IntersectionObserver' in window) {
    var sentinel = document.querySelector('[data-header-sentinel]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.setAttribute('data-header-sentinel', '');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText =
        'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
      document.body.appendChild(sentinel);
    }

    // One read, at init, off the critical path. Falls back to the token value.
    var headerH = header.offsetHeight || 68;

    new IntersectionObserver(function (entries) {
      var entry = entries[entries.length - 1];
      // Past = the sentinel has left through the TOP. Leaving through the
      // bottom (which happens on a short page during an anchor jump) must not
      // count, hence the boundingClientRect test.
      var past = !entry.isIntersecting && entry.boundingClientRect.top <= 0;
      header.classList.toggle('is-scrolled', past);
    }, { rootMargin: (-headerH) + 'px 0px 0px 0px', threshold: 0 }).observe(sentinel);
  }

  /* ── Header Hide-on-Scroll — DESKTOP POINTERS ONLY ──
     Direction genuinely needs the last scroll position, so this is the one
     thing left that wants a scroll listener, and it joins the shared one.

     It does not run on phones. Two reasons, both from the audit: a nav that
     disappears is a nuisance on a small screen where the cart button is the
     only way back, and on mobile the header is inside a section wrapper that
     also contains the build strip, so `position: sticky` un-sticks after ~73px
     and the bar it was hiding is not on screen to hide. Off a touch device,
     this file now attaches NO scroll listener at all on the homepage. */
  if (header && finePointer()) {
    var lastY = window.scrollY || 0;
    onScroll(function () {
      var y = window.scrollY || 0;
      // Hiding the bar IS travel, and it is travel of the one element that is
      // on screen the whole time. Under reduced motion it simply stays put.
      if (reduceMotion()) {
        header.classList.remove('is-hidden');
      } else if (y > 160 && y > lastY) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastY = y;
    });
    onMediaChange(motionQuery, function () {
      if (reduceMotion()) header.classList.remove('is-hidden');
    });
  }

  /* ── Cart Count Pop on Change ── */
  var cartCount = document.querySelector('.panda-header__cart-count');
  if (cartCount) {
    var observer = new MutationObserver(function () {
      cartCount.classList.remove('is-popping');
      void cartCount.offsetWidth; // restart animation
      cartCount.classList.add('is-popping');
    });
    observer.observe(cartCount, { childList: true, characterData: true, subtree: true });
  }

  /* ── Announcement Bar Dismiss ── */
  var announceDismiss = document.querySelector('[data-announce-dismiss]');
  var announceBar = document.querySelector('[data-announce-bar]');

  if (announceDismiss && announceBar) {
    announceDismiss.addEventListener('click', function () {
      announceBar.style.display = 'none';
      try { sessionStorage.setItem('panda-announce-dismissed', 'true'); } catch (err) {}
    });

    var dismissed = false;
    try { dismissed = sessionStorage.getItem('panda-announce-dismissed') === 'true'; } catch (err) {}
    if (dismissed) announceBar.style.display = 'none';
  }

  /* ── Public surface ── */
  window.PandaMotion = {
    reveal: initReveals,
    reduceMotion: reduceMotion,
    finePointer: finePointer,
    onScroll: onScroll,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll
  };
})();
